import type React from "react"
import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import {
  Box,
  Button,
  Paper,
  Grid,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material"
import api from "../services/api"
import type { AxiosError } from "axios"
import type { FormField } from "../interfaces"
import HorseLoader from "./loadingHorse"
import { useCryptoNavigation, useAppNavigation } from "../App"

interface EntityFormProps {
  title: string
  endpoint: string
  fields: FormField[]
  listPath: string
  listPageName?: string
  renderField: (props: any) => React.ReactNode
  transformBeforeSave?: (data: any) => any
  transformAfterLoad?: (data: any) => any
  validateForm?: (data: any) => Record<string, string>
  idField?: string
  allowDelete?: boolean
  deleteConfirmationText?: string
  submitButtonText?: string
  cancelButtonText?: string
  deleteButtonText?: string
  formDefaultValues?: string
  isEditMode?: boolean
  onCancel?: () => void
  onError?: (data: any) => any
  onSuccess?: (data: any) => any
  editId?: string
  useEncryptedNavigation?: boolean
  encryptedPageName?: string
  defaultTenantId?: string
  onBeforeDelete?: (data: any) => any
}

const EntityForm: React.FC<EntityFormProps> = ({
  title,
  endpoint,
  fields,
  listPath,
  listPageName,
  renderField,
  transformBeforeSave = (data) => data,
  transformAfterLoad = (data) => data,
  validateForm,
  idField = "id",
  allowDelete = true,
  deleteConfirmationText,
  submitButtonText,
  cancelButtonText,
  deleteButtonText,
  useEncryptedNavigation = true,
  defaultTenantId = "1",
}) => {
  const { t } = useTranslation()
  const { navigateToPage } = useCryptoNavigation()
  const { getParam } = useAppNavigation()

  const id = getParam('id')
  const mode = getParam('mode')
  const tenantId = getParam('tenantId') || defaultTenantId

  const isEditMode = mode === 'edit' && Boolean(id)

  const [formData, setFormData] = useState<Record<string, any>>({})
  const [originalData, setOriginalData] = useState<Record<string, any>>({})
  const [modifiedFields, setModifiedFields] = useState<Set<string>>(new Set())
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState<boolean>(isEditMode)
  const [saving, setSaving] = useState<boolean>(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [formSuccess, setFormSuccess] = useState<string | null>(null)
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [isMobile, setIsMobile] = useState(false)

  console.log("EntityForm DEBUG:", {
    id,
    mode,
    tenantId,
    isEditMode,
    modifiedFields: Array.from(modifiedFields)
  })

  useEffect(() => {
    if (!isEditMode) {
      const preFilledData: Record<string, any> = {}

      fields.forEach((field) => {
        const paramValue = getParam(field.name)
        if (paramValue) {
          if (field.type === "select") {
            preFilledData[field.name] = Number(paramValue)
          } else {
            preFilledData[field.name] = paramValue
          }
        }
      })

      if (tenantId && !preFilledData.tenantId) {
        preFilledData.tenantId = tenantId
      }

      if (Object.keys(preFilledData).length > 0) {
        setFormData((prev) => ({
          ...prev,
          ...preFilledData,
        }))
      }
    }
  }, [fields, isEditMode, getParam, tenantId])

  useEffect(() => {
    if (isEditMode && id) {
      fetchEntityData()
    }
  }, [id, endpoint, isEditMode])

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }

    window.addEventListener("resize", handleResize)
    handleResize()

    return () => window.removeEventListener("resize", handleResize)
  }, [])

  const fetchEntityData = async () => {
    try {
      setLoading(true)
      setFormError(null)

      const cleanEndpoint = endpoint.startsWith("/") ? endpoint.substring(1) : endpoint;
const apiUrl = cleanEndpoint.includes(":tenantId")
  ? cleanEndpoint.replace(":tenantId", tenantId) + `/${id}`
  : `${cleanEndpoint}/${id}`;


      console.log("Fazendo GET para:", apiUrl)
      const response = await api.get(apiUrl)

      console.log("Entity data loaded:", response.data)

      const transformedData = transformAfterLoad(response.data)
      setFormData(transformedData)
      setOriginalData(transformedData)
      setModifiedFields(new Set())
    } catch (error) {
      console.error("Error fetching entity data:", error)

      const axiosError = error as AxiosError

      if (axiosError.response) {
        if (axiosError.response.status === 404) {
          setFormError(t("form.itemNotFound"))
        } else {
          setFormError(t("form.loadError", { detail: (axiosError.response.data as any)?.message || axiosError.response.status }))
        }
      } else {
        setFormError(t("form.loadConnectionError"))
      }
    } finally {
      setLoading(false)
    }
  }

  const defaultValidateForm = (): boolean => {
    const newErrors: Record<string, string> = {}
    let isValid = true

    fields.forEach((field) => {
      if (
        field.required &&
        (formData[field.name] === undefined || formData[field.name] === null || formData[field.name] === "")
      ) {
        newErrors[field.name] = t("form.requiredField")
        isValid = false
      }
    })

    setErrors(newErrors)
    return isValid
  }

  const hasValueChanged = (originalValue: any, currentValue: any): boolean => {
    if (originalValue === currentValue) return false
    if (originalValue == null && currentValue == null) return false
    if (originalValue == null || currentValue == null) return true
    
    if (typeof originalValue === 'number' || typeof currentValue === 'number') {
      return Number(originalValue) !== Number(currentValue)
    }
    
    if (typeof originalValue === 'string' || typeof currentValue === 'string') {
      return String(originalValue) !== String(currentValue)
    }
    
    return JSON.stringify(originalValue) !== JSON.stringify(currentValue)
  }

  const getModifiedData = (): Record<string, any> => {
    const modifiedData: Record<string, any> = {}
    
    if (formData[idField]) {
      modifiedData[idField] = formData[idField]
    }
    
    modifiedFields.forEach(fieldName => {
      if (hasValueChanged(originalData[fieldName], formData[fieldName])) {
        modifiedData[fieldName] = formData[fieldName]
      }
    })
    
    return modifiedData
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (validateForm) {
      const customErrors = validateForm(formData)
      if (Object.keys(customErrors).length > 0) {
        setErrors(customErrors)
        return
      }
    } else if (!defaultValidateForm()) {
      return
    }

    setSaving(true)
    setFormError(null)
    setFormSuccess(null)

    try {
      // Determinar dados base
      let baseData: any
      
      if (isEditMode) {
        const modifiedData = getModifiedData()
        const modifiedKeys = Object.keys(modifiedData).filter(k => k !== idField)
        
        if (modifiedKeys.length === 0) {
          setFormError(t("form.noChanges"))
          setSaving(false)
          return
        }
        
        baseData = modifiedData
        console.log("Modo edição - campos modificados:", modifiedData)
      } else {
        baseData = { ...formData }
        console.log("Modo criação - todos os dados:", baseData)
      }

      // Aplicar transformação
      console.log("=== ANTES DO TRANSFORM ===")
      console.log("Dados:", JSON.stringify(baseData, null, 2))
      
      let dataToSave = transformBeforeSave(baseData)
      
      console.log("=== DEPOIS DO TRANSFORM ===")
      console.log("Dados:", JSON.stringify(dataToSave, null, 2))
      
      // CORREÇÃO CRÍTICA: Garantir que não há conflito entre tenantId e stableTenant
      if (dataToSave.tenantId !== undefined && dataToSave.stableTenant === undefined) {
        console.log("Convertendo tenantId -> stableTenant:", dataToSave.tenantId)
        dataToSave.stableTenant = dataToSave.tenantId
        delete dataToSave.tenantId
      }
      
      // Remover qualquer tenantId residual se houver stableTenant
      if (dataToSave.stableTenant !== undefined && dataToSave.tenantId !== undefined) {
        console.log("Removendo tenantId duplicado, mantendo apenas stableTenant")
        delete dataToSave.tenantId
      }
      
      console.log("=== PAYLOAD FINAL PARA API ===")
      console.log(JSON.stringify(dataToSave, null, 2))

      const cleanEndpoint = endpoint.startsWith("/") ? endpoint.substring(1) : endpoint

let apiUrl: string

if (isEditMode) {
  // PUT - Edição
  // Verificar se o endpoint já tem o tenantId
  if (cleanEndpoint.includes(tenantId)) {
    // Já tem tenantId, só adicionar o id
    apiUrl = `${cleanEndpoint}/${id}`
  } else {
    // Não tem tenantId, adicionar ambos
    apiUrl = `${cleanEndpoint}/${tenantId}/${id}`
  }
  
  console.log("PUT para:", apiUrl)
  await api.put(apiUrl, dataToSave)
  setFormSuccess(t("form.updateSuccess"))
} else {
  // POST - Criação
  // Verificar se o endpoint já tem o tenantId
  if (cleanEndpoint.includes(tenantId)) {
    // Já tem tenantId, usar como está
    apiUrl = cleanEndpoint
  } else {
    // Não tem tenantId, adicionar
    apiUrl = `${cleanEndpoint}/${tenantId}`
  }
  
  console.log("POST para:", apiUrl)
  await api.post(apiUrl, dataToSave)
  setFormSuccess(t("form.createSuccess"))
}
      setTimeout(() => {
        handleBackToList()
      }, 1500)
    } catch (error) {
      console.error("Error saving entity:", error)

      const axiosError = error as AxiosError

      if (axiosError.response) {
        if (axiosError.response.status === 400 && (axiosError.response.data as any)?.errors) {
          const backendErrors = (axiosError.response.data as any).errors
          const formattedErrors: Record<string, string> = {}

          Object.keys(backendErrors).forEach((key) => {
            if (typeof backendErrors[key] === "string") {
              formattedErrors[key] = backendErrors[key]
            } else if (Array.isArray(backendErrors[key])) {
              formattedErrors[key] = backendErrors[key].join(", ")
            }
          })

          setErrors(formattedErrors)
        } else if ((axiosError.response.data as any)?.error) {
          setFormError(t("form.saveError", { detail: (axiosError.response.data as any)?.error }))
        } else {
          setFormError(t("form.saveError", { detail: (axiosError.response.data as any)?.message || t("common.unknownError") }))
        }
      } else if (axiosError.request) {
        setFormError(t("form.saveConnectionError"))
      } else {
        setFormError(t("form.saveGenericError"))
      }
    } finally {
      setSaving(false)
    }
  }

  const handleChange = (name: string, value: any) => {
    setFormData((prev) => ({ ...prev, [name]: value }))

    if (isEditMode && hasValueChanged(originalData[name], value)) {
      setModifiedFields((prev) => new Set(prev).add(name))
    } else if (isEditMode && modifiedFields.has(name)) {
      const newSet = new Set(modifiedFields)
      newSet.delete(name)
      setModifiedFields(newSet)
    }

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }))
    }
  }

  const handleBackToList = () => {
    if (useEncryptedNavigation && listPageName) {
      const params: any = { mode: 'list' }
      
      if (tenantId && tenantId !== '1') {
        params.tenantId = tenantId
      }
      
      navigateToPage(listPageName, params)
    } else {
      window.location.href = listPath
    }
  }

  const handleCancel = () => {
    handleBackToList()
  }

  const handleOpenDeleteDialog = () => {
    setOpenDeleteDialog(true)
  }

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false)
  }

  const handleDelete = async () => {
    if (!isEditMode || !id) return

    try {
      setSaving(true)
      
      const cleanEndpoint = endpoint.startsWith("/") ? endpoint.substring(1) : endpoint
      
      const apiUrl = endpoint.includes('/:tenantId') || endpoint.includes('/1')
        ? `${cleanEndpoint.replace(':tenantId', tenantId).replace('/1', `/${tenantId}`)}/${id}`
        : `${cleanEndpoint}/${id}`

      console.log("DELETE para:", apiUrl)
      await api.delete(apiUrl)
      
      handleBackToList()
    } catch (error) {
      console.error("Erro ao excluir:", error)
      setFormError(t("form.deleteError"))
    } finally {
      setSaving(false)
      setOpenDeleteDialog(false)
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 400 }}>
        <HorseLoader />
      </Box>
    )
  }

  return (
    <Box sx={{ width: "100%", padding: 2 }}>
      <Paper sx={{ padding: 3 }}>
        {formError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {formError}
          </Alert>
        )}

        {formSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {formSuccess}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={isMobile ? 1 : 2}>
            {fields.map((field) => {
              const disabled = typeof field.disabled === "function" ? field.disabled(formData) : field.disabled

              const gridSize = isMobile
                ? 12
                : field.fullWidth === false
                  ? 6
                  : 12

              return (
                <Grid item xs={12} sm={gridSize} key={field.name}>
                  {renderField({
                    field: { ...field, disabled },
                    value: formData[field.name],
                    onChange: handleChange,
                    error: errors[field.name],
                    isEditMode: isEditMode,
                    tenantId: tenantId,
                  })}
                </Grid>
              )
            })}
          </Grid>

          <Box
            sx={{
              mt: 3,
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "space-between",
            }}
          >
            <Button
              onClick={handleCancel}
              variant="outlined"
              disabled={saving}
              sx={{ mb: isMobile ? 2 : 0 }}
              fullWidth={isMobile}
            >
              {cancelButtonText || t("common.cancel")}
            </Button>

            <Box
              sx={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                width: isMobile ? "100%" : "auto",
              }}
            >
              {isEditMode && allowDelete && (
                <Button
                  onClick={handleOpenDeleteDialog}
                  color="error"
                  variant="outlined"
                  sx={{ mr: isMobile ? 0 : 2, mb: isMobile ? 2 : 0 }}
                  disabled={saving}
                  fullWidth={isMobile}
                >
                  {deleteButtonText || t("common.delete")}
                </Button>
              )}

              <Button type="submit" variant="contained" color="primary" disabled={saving} fullWidth={isMobile}>
                {saving ? t("form.saving") : submitButtonText || (isEditMode ? t("common.update") : t("common.save"))}
              </Button>
            </Box>
          </Box>
        </form>
      </Paper>

      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{t("entity.confirmDeleteTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">{deleteConfirmationText || t("entity.deleteMessage")}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            {t("common.cancel")}
          </Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            {t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default EntityForm